import { computed, inject, Injector, Type } from '@angular/core';
import { DocumentsService } from '@data-access/documents/services';
import {
  DocumentAttribute,
  DocumentDetailSignalState,
  DocumentTypeExtended,
  EditDocument,
  UpdateDocument,
} from '@data-access/documents/types';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { SUBMITTED_DOCUMENT_DETAIL_STATE } from '@data-access/documents/contants';
import {
  DIALOG_TYPE,
  OpenDialogState,
  withDialogFeatures,
} from '@data-access/shared';
import { withDocumentTypeEntities } from '../features/with-document-types.entities';
import { withAttributesEntities } from '../features/with-attributes.entities';
import { EntityType, SubmissionResponse } from '@data-access/submissions';
import { documentFeatureMethods } from './util';

export const DOCUMENT_DETAIL_SIGNAL_STORE = signalStore(
  withState(() => inject(SUBMITTED_DOCUMENT_DETAIL_STATE)),
  withProps((store, _documentService = inject(DocumentsService)) => ({
    _documentService,
    _injector: inject(Injector),
  })),
  withFeature(() => withDialogFeatures()),
  withFeature((store) => withDocumentTypeEntities(store._injector)),
  withFeature(({ _injector }) => withAttributesEntities(_injector)),
  withFeature(documentFeatureMethods),
  withComputed(
    ({
      canDialogClose,
      data,
      documentError,
      documentLoading,
      documentSrc,
      error,
      loading,
      _documentTypesEntities,
      _attributeEntities,
    }) => ({
      $attributeTypes: computed(() => {
        const _attributeTypes = _attributeEntities();
        if (!_attributeTypes) {
          return [] as DocumentAttribute[];
        }
        return _attributeTypes;
      }),
      $canDialogClose: computed(() => canDialogClose()),
      $document: computed(() => data()),
      $documentError: computed(() => documentError()),
      $documentLoading: computed(() => documentLoading()),
      $documentSrc: computed(() => documentSrc()),
      $documentTypes: computed(() => {
        const _documentTypes = _documentTypesEntities();
        if (!_documentTypes) {
          return [] as DocumentTypeExtended[];
        }
        return _documentTypes;
      }),
      $error: computed(() => error()),
      $isLoading: computed(() => loading()),
    }),
  ),
  withMethods((store, _documentService = inject(DocumentsService)) => {
    /** Load Document, then its preview blob on the same cancellation scope **/
    const _loadDocument = rxMethod<string>(
      pipe(
        tap(() =>
          patchState(store, {
            documentError: null,
            documentLoading: true,
            documentSrc: null,
            error: null,
            loading: true,
          }),
        ),
        switchMap((documentId) =>
          _documentService.getDocumentById(documentId).pipe(
            tapResponse({
              complete: () =>
                patchState(store, {
                  loading: false,
                }),
              error: (error: Error) =>
                patchState(store, {
                  documentError: error,
                  documentLoading: false,
                  error,
                  loading: false,
                }),
              next: (data) =>
                patchState(store, {
                  data,
                }),
            }),
            switchMap(() =>
              _documentService.downloadDocument(documentId).pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, {
                      documentError: error,
                      documentLoading: false,
                      documentSrc: null,
                    }),
                  next: ({ blob }) =>
                    patchState(store, {
                      documentLoading: false,
                      documentSrc: blob,
                    }),
                }),
              ),
            ),
          ),
        ),
      ),
    );

    /**
     * Update Document
     * @param document - The document to be updated
     * @param submission - Partial submission data
     * @return Observable<DocumentResponse>
     */
    const _updateDocument = rxMethod<{
      document: EditDocument;
      submission: Partial<SubmissionResponse>;
    }>(
      pipe(
        tap(() => patchState(store, { error: null, loading: true })),
        switchMap(({ document, submission }) => {
          let _document: UpdateDocument = {
            document_type_id: document.document_type?.id || '',
            task_ids:
              (document.tasks || [])
                .map((task) => task.id)
                .filter((id): id is string => id !== undefined) || [],
          };

          if (document.wcg_name) {
            _document = {
              ..._document,
              attribute_type_id: document.attribute_type?.id || '',
              wcg_name: document.wcg_name,
            };
          }

          return _documentService
            .updateDocument(document.id, _document, submission)
            .pipe(
              tapResponse({
                complete: () =>
                  patchState(store, {
                    loading: false,
                  }),
                error: (error: Error) =>
                  patchState(store, {
                    error,
                  }),
                next: (data) =>
                  /**
                   * On successful update, we patch the state with the new document data
                   * while preserving the next and previous document IDs from the current state.
                   */
                  patchState(store, {
                    canDialogClose: true,
                    data: {
                      ...data,
                      next: store.data().next,
                      previous: store.data().previous,
                    },
                  }),
              }),
            );
        }),
      ),
    );

    const gotoNext = () => {
      const nextId = store.data.next();
      if (nextId) {
        _loadDocument(nextId);
      }
    };

    const gotoPrev = () => {
      const prevId = store.data.previous();
      if (prevId) {
        _loadDocument(prevId);
      }
    };

    const _openEditDocumentDialog = (dialogMetaData: OpenDialogState) => {
      store.openDialog({
        component: () => dialogMetaData.component(),
        componentData: dialogMetaData.componentData,
        type: DIALOG_TYPE.GroupFields,
      });
    };
    const _markCompleteDocument = rxMethod<{
      documentId: string;
      complete: boolean;
    }>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        switchMap(({ documentId, complete }) => {
          return _documentService
            .markCompleteDocument(documentId, complete)
            .pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error, loading: false }),
                next: (documentResponse) => {
                  const currentDocument = store.data();
                  const updatedDocument = {
                    ...documentResponse,
                    previous: currentDocument?.previous,
                    next: currentDocument?.next,
                  };

                  patchState(store, {
                    data: updatedDocument,
                    loading: false,
                    error: null,
                  });
                },
              }),
            );
        }),
      ),
    );

    return {
      deleteDocument: (
        workspaceId: string,
        currentStage: string,
        documentId: string,
        onSuccess?: () => void,
      ) =>
        store.applyDeleteDocument(
          workspaceId,
          currentStage,
          documentId,
          onSuccess,
        ),
      downloadDocument: (documentId: string, filename: string) =>
        store.downloadDocument(documentId, filename),
      gotoNext,
      gotoPrev,
      loadAttributeTypes: (params: { entityType?: EntityType }) => {
        store.setAttributes(params);
      },
      loadDocument: (documentId: string) => _loadDocument(documentId),
      loadDocumentTypes: (params: { workspaceId: string }) => {
        store.setDocumentTypes(params);
      },
      markCompleteDocument: (document: {
        documentId: string;
        complete: boolean;
      }) => _markCompleteDocument(document),
      openDocumentInNewTab: (documentId: string) =>
        store.openInNewTab(documentId),
      openEditDocumentDialog: (dialogMetaData: OpenDialogState) => {
        patchState(store, { canDialogClose: false });
        _openEditDocumentDialog(dialogMetaData);
      },
      updateDocument: (data: {
        document: EditDocument;
        submission: Partial<SubmissionResponse>;
      }) => _updateDocument(data),
    };
  }),
) satisfies Type<DocumentDetailSignalState>;
